import React, { useEffect, useState } from 'react';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import { api } from '../api';
import { C } from '../common';
import { Card, Donut, GoldBar, Kpi } from './ui';

type Dash = {
  year: number; teachers: number; churches: number; issuedTotal: number; issuedThisYear: number; issuedPersons: number;
  issueRate: number; pendingReports: number; rejectedReports: number; churchReceived: number; churchNotReceived: number;
  dataIssues: { noClub: number; noEnglishName: number; duplicates: number; badPhone: number };
  clubStats: Array<{ club: string; label: string; teachers: number; issued: number }>;
};

const DashboardTab: React.FC<{ go: (tab: string, extra?: Record<string, string>) => void }> = ({ go }) => {
  const [data, setData] = useState<Dash | null>(null);
  const [error, setError] = useState('');
  useEffect(() => { api<{ data: Dash }>('/admin/edu/dashboard').then((r) => setData(r.data)).catch((e) => setError(e.message)); }, []);
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!data) return <Box sx={{ py: 8, display: 'grid', placeItems: 'center' }}><CircularProgress /></Box>;
  const d = data.dataIssues;
  return (
    <Box sx={{ display: 'grid', gap: 2 }}>
      <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(6, 1fr)' } }}>
        <Kpi label="교사 (동일인 기준)" value={data.teachers.toLocaleString()} sub={`교회 ${data.churches}곳`} onClick={() => go('churches')} />
        <Kpi label={`${data.year}년 수료증 발급`} value={data.issuedThisYear} sub={`누적 ${data.issuedTotal}장`} tone="ok" onClick={() => go('certs')} />
        <Kpi label="발급률" value={`${data.issueRate}%`} sub={`${data.issuedPersons}명 보유`} tone="ok" />
        <Kpi label="완성보고서 처리 대기" value={data.pendingReports} sub="장 (교사 1명 = 1장)" tone={data.pendingReports ? 'warn' : 'plain'} onClick={() => go('reports')} />
        <Kpi label="반려 (재제출 대기)" value={data.rejectedReports} tone={data.rejectedReports ? 'err' : 'plain'} onClick={() => go('reports', { status: 'rejected' })} />
        <Kpi label="교회 미수령" value={data.churchNotReceived} sub={`수령 ${data.churchReceived}`} tone={data.churchNotReceived ? 'warn' : 'plain'} onClick={() => go('certs', { receipt: 'none' })} />
      </Box>

      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1.6fr 1fr' } }}>
        <Card title="클럽별 수료 현황">
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '150px 1fr' }, gap: 2, alignItems: 'center' }}>
            <Box sx={{ display: 'grid', placeItems: 'center' }}>
              <Donut pct={data.issueRate} />
              <Typography sx={{ fontSize: 12, color: C.sub, mt: 1 }}>전체 교사 대비 수료증 보유</Typography>
            </Box>
            <Box>
              {data.clubStats.map((s) => <GoldBar key={s.club} label={s.label} done={s.issued} total={s.teachers} />)}
              <Typography sx={{ fontSize: 11.5, color: C.sub, mt: 1 }}>교사에 등록된 담당 클럽 기준 · 한 교사가 여러 클럽이면 각각 집계</Typography>
            </Box>
          </Box>
        </Card>
        <Card title="데이터 정리 필요">
          {[
            ['클럽 미지정', d.noClub, 'noClub'],
            ['영문 이름 없음', d.noEnglishName, 'noEn'],
            ['중복 등록 (같은 이름+휴대폰)', d.duplicates, 'dup'],
            ['휴대폰 번호 확인', d.badPhone, 'phone'],
          ].map(([label, n, key]) => (
            <Box key={key as string} onClick={() => go('churches', { issue: key as string })}
              sx={{ display: 'flex', justifyContent: 'space-between', py: 1.1, borderBottom: `1px solid ${C.line}`, cursor: 'pointer', '&:hover': { bgcolor: '#fffbe8' } }}>
              <Typography sx={{ fontSize: 14 }}>{label}</Typography>
              <Typography sx={{ fontSize: 14, fontWeight: 800, color: Number(n) ? C.warn : C.sub }}>{Number(n).toLocaleString()}명 ›</Typography>
            </Box>
          ))}
          <Typography sx={{ fontSize: 12, color: C.sub, mt: 1.5 }}>영문 이름은 수료증 영문 표기에 쓰이며, 보고서 승인 때 입력하면 교사 정보에 자동 저장됩니다.</Typography>
        </Card>
      </Box>
    </Box>
  );
};

export default DashboardTab;
